import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { tenantsApi } from "@/api/endpoints";
import { useThemePref } from "@/store/theme";

/**
 * Feeds the tenant's house palette into the theme store — level 3 of the precedence chain.
 *
 * Call it from a component that only renders behind `AuthGuard`: `/tenants/current` answers 401
 * when logged out, and `ThemedApp` sits above the login screen. `Layout` is the right home.
 *
 * The policy is cached in the same persisted store, so a reload applies the house style
 * immediately rather than flashing the previous palette while this request is in flight. This
 * refresh is what catches an admin *changing* it.
 *
 * Shares the `["tenant", "current"]` query key with the tenant settings and appearance pages, so
 * saving on the appearance page invalidates this too and no extra request is made on navigation.
 */
export function useTenantAppearance(): void {
  const setTenantPolicy = useThemePref((s) => s.setTenantPolicy);

  const { data } = useQuery({
    queryKey: ["tenant", "current"],
    queryFn: tenantsApi.current,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!data) return;
    setTenantPolicy(data.defaultThemeId, data.allowUserThemeOverride);
  }, [data, setTenantPolicy]);
}
